import i18next from '../i18n/initI18n';
import { clearAuthToken, getSmsCabinetUrl, getStoredAuthToken } from '../auth/cascadeAuth';

const BASE = '';

let cabinetReauthRedirectScheduled = false;

function redirectToCabinetReauthOnce(): void {
  if (typeof window === 'undefined' || cabinetReauthRedirectScheduled) return;
  cabinetReauthRedirectScheduled = true;
  try {
    clearAuthToken();
  } catch {
    /* ignore */
  }
  const u = getSmsCabinetUrl().trim();
  if (u) {
    window.location.assign(u);
    return;
  }
  window.location.reload();
}

function parseJsonErrorPayload(text: string): { extracted?: string } {
  try {
    const j = JSON.parse(text) as {
      error?: string;
      detail?: unknown;
    };
    const d = j?.detail;
    if (typeof d === 'string' && d.trim()) return { extracted: d.trim() };
    if (Array.isArray(d) && d.length > 0) {
      const first = d[0] as { msg?: string };
      if (typeof first?.msg === 'string' && first.msg.trim()) return { extracted: first.msg.trim() };
    }
    if (typeof j?.error === 'string' && j.error.trim()) return { extracted: j.error.trim() };
  } catch {
    /* */
  }
  return {};
}

/** Сообщение для UI из тела ответа middleware (`error`) или FastAPI (`detail`). */
function resolvedClientErrorMessage(text: string, statusTextFallback: string): string {
  const { extracted } = parseJsonErrorPayload(text);
  let msg = extracted || statusTextFallback;
  if (
    msg === 'Internal Server Error' ||
    msg === 'Bad Gateway' ||
    msg === 'Service Unavailable' ||
    msg === 'Gateway Timeout'
  ) {
    msg = i18next.t('api.backendUnavailable');
  }
  return msg;
}

function httpError(msg: string, status: number): Error {
  const e = new Error(msg) as Error & { status: number };
  e.status = status;
  return e;
}

function mergeAuthHeaders(base?: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = { ...(base || {}) };
  const t = getStoredAuthToken();
  if (t?.trim()) out.Authorization = `Bearer ${t.trim()}`;
  return out;
}

function clearTokenIf401(status: number): void {
  if (status !== 401) return;
  redirectToCabinetReauthOnce();
}

/** Ответ не JSON (часто index.html, если не запущен API на :4001). */
function isLikelyHtml(text: string): boolean {
  const t = text.trim();
  return t.startsWith('<!') || t.startsWith('<html') || (t.startsWith('<') && t.includes('<!DOCTYPE'));
}

function safeParseJson(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error(i18next.t('api.emptyResponse'));
  }
  if (isLikelyHtml(text)) {
    throw new Error(i18next.t('api.htmlInsteadJson'));
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      trimmed.length > 200
        ? i18next.t('api.notJson')
        : i18next.t('api.badJson', { snippet: trimmed.slice(0, 160) })
    );
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: mergeAuthHeaders(),
  });
  const text = await res.text();
  if (!res.ok) {
    clearTokenIf401(res.status);
    if (isLikelyHtml(text)) {
      throw httpError(i18next.t('api.htmlInsteadJsonShort'), res.status);
    }
    const msg = resolvedClientErrorMessage(text, res.statusText);
    throw httpError(msg, res.status);
  }
  return safeParseJson(text) as T;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: mergeAuthHeaders({ 'Content-Type': 'application/json' }),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  if (!res.ok) {
    clearTokenIf401(res.status);
    const msg = resolvedClientErrorMessage(text, res.statusText);
    throw httpError(msg, res.status);
  }
  return safeParseJson(text) as T;
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: mergeAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    clearTokenIf401(res.status);
    const msg = resolvedClientErrorMessage(text, res.statusText);
    throw httpError(msg, res.status);
  }
  return safeParseJson(text) as T;
}

export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: mergeAuthHeaders(),
  });
  const text = await res.text();
  if (res.ok) return;
  clearTokenIf401(res.status);
  if (isLikelyHtml(text)) {
    throw httpError(i18next.t('api.htmlInsteadJsonDev'), res.status);
  }
  const msg = resolvedClientErrorMessage(text, res.statusText);
  throw httpError(msg, res.status);
}
